import { useEffect, useRef } from 'react';

interface UseVoiceTranscriptOptions {
  isListening: boolean;
  transcript: string;
  value: string;
  setValue: (value: string) => void;
  expandIfOverflowing: () => void;
}

export const useVoiceTranscript = ({
  isListening,
  transcript,
  value,
  setValue,
  expandIfOverflowing,
}: UseVoiceTranscriptOptions): void => {
  const valueBeforeListeningRef = useRef('');

  // Capture existing text when listening starts, clear when it stops.
  // Intentionally excludes `value` from deps: we want to snapshot the value at
  // the moment listening begins, not re-capture on every keystroke.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional snapshot on isListening toggle
  useEffect(() => {
    if (isListening) {
      valueBeforeListeningRef.current = value;
    } else {
      valueBeforeListeningRef.current = '';
    }
  }, [isListening]);

  // Append transcript to the captured pre-listening text and auto-expand if it overflows
  useEffect(() => {
    if (transcript) {
      const prefix = valueBeforeListeningRef.current;
      const separator = prefix && !prefix.endsWith(' ') ? ' ' : '';
      setValue(prefix + separator + transcript);
      expandIfOverflowing();
    }
  }, [transcript, setValue, expandIfOverflowing]);
};
